<template>
  <v-card>
    <div class="reviewer-section">
      <div class="profile-avatar-background">
        <router-link
          :to="{
            name: 'profile',
            params: { username: review.author.username },
          }"
        >
          <profile-avatar
            :username="review.author.username"
            :preset-profile-pic="review.author.profilePicture"
            :size="40"
          />
        </router-link>
      </div>
      <div class="reviewer-info">
        <div class="reviewer-username">
          <router-link
            :to="{
              name: 'profile',
              params: { username: review.author.username },
            }"
          >
            <span style="font-weight: bold">@{{ review.author.username }}</span>
          </router-link>
        </div>
        <div class="reviewer-full-name">
          <router-link
            v-if="getUserFullName(review)"
            :to="{
              name: 'profile',
              params: { username: review.author.username },
            }"
          >
            {{ getUserFullName(review) }} ·
          </router-link>
          <span>posted {{ relativeDateString(new Date(review.timePosted).getTime()) }}</span>
        </div>
      </div>
      <review-utility-button
        :icon-size="24"
        :post="{
          username: review.author.username,
          id: review.id,
        }"
      ></review-utility-button>
    </div>
    <div class="poster-container">
      <router-link :to="{ path: '/work/' + review.work.medium + '/' + review.work.id }">
        <div :style="'height: ' + posterHeight + 'px'">
          <blurred-margin-poster :src="review.work.posterSource">
            <medium-icon :medium="review.work.medium" />

            <rating-circle class="rating-circle" :size="72" :rating="review.rating" :opacity="0.65"></rating-circle>
          </blurred-margin-poster>
        </div>
      </router-link>
      <div class="work-title">
        {{ review.work.title }}
      </div>
    </div>

    <div class="text-section">
      <div v-if="review.review" class="review-section">
        <div v-if="review.spoiler && !spoilerRevealed" class="spoiler-censor">
          <v-btn small @click="spoilerRevealed = true">Contains Spoilers</v-btn>
        </div>
        <p ref="review-text" class="review-text">{{ review.review }}</p>
        <button v-if="reviewIsTruncated" class="expand-button" @click="expandReview">
          Show
          <span v-if="reviewIsExpanded">Less</span>
          <span v-else>More</span>
        </button>
      </div>

      <v-divider v-if="review.review"></v-divider>

      <div class="comment-section">
        <p v-for="comment in displayedComments" :key="'comment' + comment.id">
          <router-link
            class="username"
            :to="{
              name: 'profile',
              params: { username: comment.author.username },
            }"
            >{{ comment.author.username }} </router-link
          ><span class="comment">{{ comment.content }}</span>
        </p>

        <button v-if="reviewHasHiddenComments" class="expand-button" @click="expandComments">View More Comments</button>
      </div>

      <div class="new-comment-input">
        <v-icon color="#999999" size="16">mdi-comment</v-icon>
        <textarea
          class="new-comment-textarea"
          v-model="newComment"
          @input="autoExpandTextarea($event)"
          placeholder="Add a comment"
          ref="new-comment-textarea"
        />
        <button @click="postComment" :disabled="!newComment" class="post-button">
          <v-icon v-if="newComment" color="blue">mdi-send-variant</v-icon>
          <v-icon v-else>mdi-send-variant-outline</v-icon>
        </button>
      </div>
    </div>

    <comments-popup
      :visible.sync="commentsPopup.expanded"
      :review="commentsPopup.review"
      :comments="commentsPopup.comments"
      @comment-posted="handleCommentPosted($event)"
    ></comments-popup>
  </v-card>
</template>

<script>
// Components
import mediumIcon from "./medium-icon.vue";
import ratingCircle from "./rating-circles/rating-circle-monochrome.vue";
import profileAvatar from "./profile-avatar.vue";
import ReviewUtilityButton from "./review-utility-button.vue";
import CommentsPopup from "./comments-popup.vue";
import BlurredMarginPoster from "./poster-blurred-margins.vue";
// Mixins
import autoExpandTextarea from "../mixins/autoExpandTextarea";
// Util
import { formatV0Comment } from "../util/comments";
import { relativeDateString } from "../util/dateHelpers";
import getAspectRatio from "../util/getAspectRatio";

export default {
  name: "home-feed-review",

  components: {
    mediumIcon,
    ratingCircle,
    profileAvatar,
    ReviewUtilityButton,
    CommentsPopup,
    BlurredMarginPoster,
  },

  mixins: [autoExpandTextarea],

  props: {
    /**
     * {
     *   id: string
     *   work: {
     *     id: string,
     *     medium: string
     *     title: string
     *     posterSource: string
     *   },
     *   review?: string
     *   rating: number
     *   spoiler: boolean
     *   dateViewed?: string, //yyyy-mm-dd
     *   timePosted: string, datetime,
     *   comments: {
     *     items: {
     *       id: string
     *       author: {
     *         id: string
     *         username: string
     *       },
     *       content: string,
     *       timePosted: datetime
     *     }[]
     *   }
     * }
     */
    review: Object,
  },

  emits: ["review-updated"],

  computed: {
    reviewElement: function () {
      if (this.$el === undefined) return undefined;
      return this.$el.querySelector(".review-text");
    },

    reviewHasHiddenComments: function () {
      let comments = this.review.comments.items;
      for (let i = 2; i < comments.length; i++) {
        if (!comments[i].visible) return true;
      }
      return false;
    },

    displayedComments: function () {
      let comments = [];
      // Show first two comments
      for (let i = 0; i < 2; i++) {
        if (this.review.comments.items.length > i) {
          comments.push(this.review.comments.items[i]);
        }
      }
      // Show comments marked as visible (such as just posted by user)
      for (let i = 2; i < this.review.comments.items.length; i++) {
        let comment = this.review.comments.items[i];
        if (comment.visible) {
          comments.push(comment);
        }
      }
      return comments;
    },

    posterHeight: function () {
      return (1 / getAspectRatio(this.review.work.medium)) * 200;
    },
  },

  data: () => {
    return {
      reviewIsTruncated: false,
      reviewIsExpanded: false,
      newComment: undefined,

      commentsPopup: {
        expanded: false,
        review: undefined,
        comments: [],
      },

      spoilerRevealed: false,
    };
  },

  mounted() {
    this.setReviewIsTruncated();
  },

  methods: {
    relativeDateString,

    setReviewIsTruncated() {
      if (!this.reviewElement) {
        this.reviewIsTruncated = false;
        return;
      }

      const REVIEW_TRUNCATED_DIFF_THRESHOLD = 4; // In pixels
      let difference = Math.abs(this.reviewElement.scrollHeight - this.reviewElement.clientHeight);
      this.reviewIsTruncated = difference > REVIEW_TRUNCATED_DIFF_THRESHOLD;
    },

    expandReview() {
      this.reviewElement.classList.toggle("expanded");
      this.reviewIsExpanded = !this.reviewIsExpanded;
    },

    expandComments() {
      this.commentsPopup = {
        expanded: true,
        review: this.review,
        comments: this.review.comments.items,
      };
    },
    getUserFullName(review) {
      let fname = review.author.firstName;
      let lname = review.author.lastName;
      if (fname && lname) {
        return `${fname} ${lname}`;
      }
      if (fname) {
        return fname;
      }
      if (lname) {
        return lname;
      }
      return "";
    },

    // event is a comment object
    handleCommentPosted(event) {
      let newReview = Object.assign({}, this.review);
      newReview.comments.items.push(event);
      this.$emit("review-updated", newReview);
    },

    postComment() {
      this.$axios
        .post("/comment", {
          review_id: this.review.id,
          content: this.newComment,
        })
        .then((response) => {
          let comment = response.data;
          comment = formatV0Comment(comment);
          comment.visible = true;
          this.handleCommentPosted(comment);
          this.newComment = "";
          this.$refs["new-comment-textarea"].style.height = "";
        });
    },
  },
};
</script>

<style>
:root {
  --post-button-color-rgb: 0, 121, 233;
  --review-tile-padding-x: 8px;
  --review-tile-border-radius: 4px;
  --review-section-margin-y: 10px;
}
</style>

<style scoped>
/* Veutify overrides*/
.v-icon.v-icon::after {
  transform: scale(1) !important;
}

.poster-container {
  position: relative;
}

.medium-icon {
  position: absolute;
  margin: 12px;
  background-color: var(--semi-opaque-overlay-bg-color) !important;
  z-index: 1;
}

.rating-circle {
  position: absolute;
  top: 0;
  right: 0;
  margin: 6px;
}

.main-poster {
  position: relative;
  z-index: 0;
}

.work-title {
  position: absolute;
  bottom: 0px;
  height: 36px;
  max-width: calc(100% - 12px);
  border-top-right-radius: 8px;
  padding: 8px;
  backdrop-filter: blur(3px);

  /* Text style */
  color: white;
  line-height: 20px;
  font-size: 20px;
  font-weight: bold;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  background-color: rgba(0, 0, 0, 0.5);

  transition: max-width 1s;
}

.work-title:hover,
.work-title:active {
  max-width: 1000px;
}

.text-section {
  position: relative;
  padding: 0 var(--review-tile-padding-x);
}

.profile-avatar-background {
  width: 40px;
  position: relative;
  margin-right: 8px;

  background-color: white;
  border-radius: 50%;
}

.reviewer-section {
  padding: 8px;
  height: 56px;
  position: relative;
  display: flex;
  color: #292929;
}
.reviewer-info {
  padding-top: 2px;
  padding-bottom: 2px;
  position: relative;
}

.reviewer-username {
  position: relative;
  font-size: 16px;
  line-height: 19px;
}

.reviewer-username-only > span {
  top: 0px;
  font-size: 12px;
  line-height: 14px;
}

.reviewer-full-name {
  position: relative;
  display: block;
  margin-top: 3px;
  padding-bottom: 2px;
  font-size: 12px;
  line-height: 14px;
}

.reviewer-section > .review-utility-button {
  display: relative;
  position: absolute;
  right: 0px;
  margin-right: 8px;
  margin-top: 4px;
  height: 32px;
}

.review-section {
  position: relative;
  margin: var(--review-section-margin-y) 0;
}

.review-text {
  margin: 0;
  max-height: 160px;

  display: -webkit-box;
  -webkit-line-clamp: 10;
  -webkit-box-orient: vertical;

  white-space: pre-wrap;
  font-size: 14px;
  line-height: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spoiler-censor {
  position: absolute;
  height: calc(100% + var(--review-section-margin-y) * 2);
  width: calc(100% + var(--review-tile-padding-x) * 2);
  left: calc(0px - var(--review-tile-padding-x));
  top: calc(0px - var(--review-section-margin-y));

  display: flex;
  align-items: center;
  justify-content: center;

  backdrop-filter: blur(3px);
}

.review-text.expanded {
  -webkit-line-clamp: unset;
  max-height: none;
  animation: expand 1s;
}

@keyframes expand {
  0% {
    max-height: 160px;
    -webkit-line-clamp: 10;
  }
  1% {
    -webkit-line-clamp: unset;
  }
  99% {
    max-height: 1000px;
  }
  100% {
    max-height: none;
    -webkit-line-clamp: unset;
  }
}

.expand-button {
  display: block;
  margin: auto;
  text-decoration: underline;
}

.review-section .expand-button {
  color: #666666;
  font-size: 14px;
  line-height: 16px;
}

.comment-section .expand-button {
  margin-top: 3px;
  color: #999999;
  font-size: 10px;
  line-height: 1;
}

.comment-section {
  font-size: 12px;
  line-height: 14px;
}

.comment-section > p {
  margin: 6px 0 0;
  white-space: pre-wrap;
  color: rgba(0, 0, 0, 0.609);
}

.comment-section > p > .username {
  color: rgb(76.5, 76.5, 76.5);
  font-weight: 600;
}

.new-comment-input {
  position: relative;
  padding: 6px 0;
  font-size: 15px;
  line-height: 1;
  box-sizing: border-box;
  display: flex;
}

.new-comment-input > .v-icon {
  margin-right: 4px;
}

.new-comment-input > textarea {
  resize: none;
  height: 22px;
  margin-left: 0px;
  padding-left: 3px;
  padding-top: 2.5px;
  font-size: 14px;
  line-height: 19px;
  flex-grow: 1;
}

.new-comment-input > textarea:focus {
  outline: none;
}
.new-comment-input > .post-button {
  font-size: 12px;
}
.new-comment-input > .post-button:hover {
  color: rgba(var(--post-button-color-rgb), 1);
}
.new-comment-input > .post-button:focus {
  outline: 1px solid rgba(var(--post-button-color-rgb), 1);
}
.new-comment-input > .post-button:active {
  color: rgba(var(--post-button-color-rgb), 0.5);
}
.new-comment-input > .post-button:disabled {
  color: rgba(var(--post-button-color-rgb), 0.5);
}
</style>
